import jsPDF from 'jspdf';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { es, fr } from 'date-fns/locale';
import {
  deriveManagedConditionNames,
  findGeneralCarePlanDefinition,
  formatDiseaseName,
  formatPdfCarePlanSectionTitle,
  formatPdfDocumentTitle,
  groupBarriersByGoal,
  isGeneralCarePlanDisease,
  PATIENT_FRIENDLY_HEADINGS,
} from './carePlanDisplay';
import { patientApi } from '../services/api';

interface Problem {
  problem: string;
  clinical_status: string;
  onset_date: string;
  // PHA-865: ICD-10 code shown in the "Full Problem List" section (PCMH clinics).
  icd10_code?: string;
  // PHA-888: import timestamp of the problem row; the most recent across the list
  // drives the "As of <date>" note under the Full Problem List header.
  uploaded_ts?: string;
}

// PHA-865: medication shape for the "Full Medication List" section (mirrors the
// Medications tab in PatientDetail.tsx). Medications have no status field — an empty
// end_date means the medication is ongoing.
interface Medication {
  medication: string;
  dosage?: string;
  start_date: string;
  end_date?: string;
  // PHA-888: import timestamp of the medication row; the most recent across the
  // list drives the "As of <date>" note under the Full Medication List header.
  uploaded_ts?: string;
}

interface Goal {
  measure: string;
  target_value: string;
  test: string;
  due_date: string;
  achieved?: boolean;
  achieved_date?: string;
}

interface Question {
  prompt: string;
  data_type: string;
  frequency: string;
}

interface Task {
  task: string;
  assignee: string;
  frequency: string;
  due_date?: string;
  completed?: boolean;
  completed_date?: string;
}

// PHA-739: Patient-friendly plan blob (mirrors PatientFriendlyPlan in types/patient.ts)
interface PatientFriendlyPlanLocal {
  goals?: string;
  medications?: { name: string; reason: string }[];
  tasks?: { instruction: string; how_often?: string }[];
  barriers?: string;
  warning_signs?: { sign: string; action: string }[];
  resources?: string[];
}

interface CarePlanDefinition {
  disease: string;
  uploaded_ts: string;
  questions?: Question[];
  tasks?: Task[];
  goals?: Goal[];
  // PHA-773: clinical_disease tags are the title fallback when diseases[] is empty.
  icd10_codes?: { code: string; description: string; clinical_disease?: string | null }[];
  /** PHA-674: official covered-disease names from care_plan_definition_diseases rollup view. */
  diseases?: string[];
  /** Authoritative backend flag: generic catch-all plan (titled "General Care Plan"). */
  is_general?: boolean;
  patient_friendly_plan?: PatientFriendlyPlanLocal | null;
  /** PHA-741/PHA-767: care plan expiration date (ISO YYYY-MM-DD); already flows from cpd_details. */
  expiration_date?: string | null;
  /** PHA-867: name (not email) of the clinician who approved this plan, snapshotted
   *  at approval time. NULL for drafts and pre-PHA-867 rows. */
  reviewed_by_name?: string | null;
}

// PHA-870: care-team Care Plan Review records (mirrors CarePlanReview in types/patient.ts).
// Rendered as a PDF section following "Your Care Plan in Details".
interface CarePlanReviewLocal {
  review_date: string | null;
  patient_comments?: string | null;
  action_taken?: string | null;
}

interface PatientData {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  patient_id: string;
  apcm_tier?: number | null;
  // PHA-1195: the patient's chosen language (patients.language). A Latin-script language
  // prints the whole care plan translated; see resolveCarePlanPdfLanguage.
  language?: string | null;
  // PHA-1164: an active RPM device assignment (patients_with_details.has_rpm_device).
  has_rpm_device?: boolean | null;
  problems?: Problem[];
  // PHA-865: full medication list, rendered as a PDF section for PCMH clinics.
  medications?: Medication[];
  // PHA-870: care-team review records, rendered after "Your Care Plan in Details".
  care_plan_reviews?: CarePlanReviewLocal[];
  care_plan_definitions?: CarePlanDefinition[];
  logo_path?: string;
  organization_name?: string;
  // PHA-737: top-level expiration fallback for draft PDFs. Drafts have no stored
  // expiration_date (set only at approve time), so the on-screen value is passed here.
  expiration_date?: string | null;
  // PHA-795 E4: Approved person-centered outcome data (from PCO tables, NOT LLM-generated JSON).
  // When person_centered_goals is present and non-empty, a "What Matters to You" section is added.
  // PHA-832: when voice_of_patient_enabled is true, the "What Matters to You" section is rendered
  // even with no goals/barriers, using explicit "No goals were defined." empty-state text.
  voice_of_patient_enabled?: boolean | null;
  // PHA-866: when true, the Health Goals table drops the Due Date / Achieved? /
  // Achievement Date columns (compliance requirement for orgs like ODA).
  no_target_dates_enabled?: boolean | null;
  // PHA-942: patient consent state ('collected' | 'denied' | null). Only a 'collected'
  // consent allows the "Printed and handed to the patient" note to render.
  consent_status?: string | null;
  // PHA-942: true only when this PDF renders an APPROVED (finalized) care plan. Set
  // exclusively by the approved-plan download path; every draft/preview path leaves it
  // undefined, so the "Printed and handed to the patient" note fails closed.
  is_finalized_plan?: boolean;
  // PHA-983: true when this render is destined for the EHR rather than a printer. It
  // suppresses the PHA-942 "Printed and handed to the patient" note, which would otherwise
  // ride along on a copy that was filed into a chart and handed to nobody — a factual claim
  // inside a permanent medical record. Everything else about the document is identical, so
  // the filed PDF still matches what the clinician reviewed.
  is_ehr_copy?: boolean;
  // PHA-1032: true for a not-yet-approved render. Drives the DRAFT banner/header/filename
  // and sources "Generated and reviewed by" from draft_reviewer_name. It does NOT change
  // the header's "Care Plan Review Date" — that is the next-plan due date on both paths.
  is_draft?: boolean;
  // PHA-1032: current logged-in user's display name, shown as "Generated and reviewed by"
  // on a draft — there is no approving clinician until approval sets reviewed_by_name.
  draft_reviewer_name?: string | null;
  person_centered_goals?: Array<{
    pco_ref: string;
    goal_text: string;
    sort_order: number;
    tracking_question?: string | null;
  }> | null;
  pcm_barriers?: Array<{
    barrier_ref: string;
    barrier_category: string;
    barrier_text: string;
    sort_order: number;
    // PHA-850/PHA-851: optional goal link. Absent in today's data; when present
    // barriers are grouped under their goal instead of a shared block.
    pco_ref?: string | null;
  }> | null;
  gas_definitions?: Array<{
    pco_ref: string;
    goal_text: string;
    level_minus2?: string | null;
    level_minus1?: string | null;
    level_zero?: string | null;
    level_plus1?: string | null;
    level_plus2?: string | null;
    baseline_level?: number | null;
  }> | null;
}

/**
 * PHA-944: the date this care plan carries, for the PDF's "Care Plan Created" row and
 * running header.
 *
 * Both used to stamp `new Date()`, so the printed plan said it was created on whatever day
 * someone clicked print — a third answer, disagreeing with both the chart card and the
 * review date. Read the plan's own timestamp instead: the approval instant once approved,
 * the generation instant while it is still a draft (or for anything approved before
 * PHA-944, whose approved_ts is null).
 *
 * Falls back to today only when there is no plan at all to read a date from, which is a
 * preview with nothing in it.
 */
export const resolveCarePlanCreatedDate = (patient: {
  care_plan_definitions?: any[];
}): string => {
  const plans = (patient?.care_plan_definitions || []).filter((p) => p?.uploaded_ts);
  if (plans.length === 0) return new Date().toISOString();
  const newest = plans.reduce((latest, plan) =>
    String(plan.uploaded_ts) > String(latest.uploaded_ts) ? plan : latest
  );
  return String(newest.approved_ts || newest.uploaded_ts);
};

const formatDate = (dateString: string, locale?: Locale): string => {
  try {
    // For date-only strings (YYYY-MM-DD), parse components directly
    // to avoid timezone conversion issues (e.g., May 2 becoming May 1 in US timezones)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return format(date, 'MMM dd, yyyy', { locale });
    }
    // For other date formats, use standard parsing
    const date = new Date(dateString);
    return format(date, 'MMM dd, yyyy', { locale });
  } catch {
    return dateString;
  }
};

// PHA-888: most recent import timestamp across a set of problem/medication rows.
// uploaded_ts is an ISO-8601 timestamp, so lexical max == chronological max.
// Returns null when no row carries an uploaded_ts.
const mostRecentUploadedTs = (
  items: Array<{ uploaded_ts?: string }>
): string | null => {
  const stamps = items.map((i) => i.uploaded_ts).filter((t): t is string => !!t);
  if (stamps.length === 0) return null;
  return stamps.reduce((a, b) => (a > b ? a : b));
};

// PHA-1291 stopgap: patients whose EHR chart MRN differs from their Phanova patient_id,
// because the real MRN is already another clinic's patient. The footer must show the MRN the
// chart has. Keep in sync with EHR_MRN_OVERRIDES in app_logic.py; remove with PHA-1293.
const EHR_MRN_OVERRIDES: Record<string, string> = {
  MRN59516467: '15865',
};

// PHA-1293: a patient whose MRN was already another EHR's patient_id is keyed "<mrn>|<n>"
// (PHA-986). The chart's MRN is the part before the first "|" (an id reserved twice can be
// "<mrn>|<n>|<m>"): the same value the backend stores in patient_organization.mrn and sends
// on every EHR call.
const INTERNAL_ID_SUFFIX = /^([^|]+)\|\d/;

export const ehrMrnFor = (patientId: string): string => {
  if (EHR_MRN_OVERRIDES[patientId]) return EHR_MRN_OVERRIDES[patientId];
  const match = INTERNAL_ID_SUFFIX.exec(patientId);
  return match ? match[1] : patientId;
};

// Helper function to sanitize text for jsPDF (replace unsupported Unicode characters)
const sanitizeTextForPdf = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/≥/g, '>=')    // Greater than or equal
    .replace(/≤/g, '<=')    // Less than or equal
    .replace(/≠/g, '!=')    // Not equal
    .replace(/±/g, '+/-')   // Plus-minus
    .replace(/×/g, 'x')     // Multiplication
    .replace(/÷/g, '/')     // Division
    .replace(/→/g, '->')    // Arrow
    .replace(/←/g, '<-')    // Left arrow
    .replace(/•/g, '-')     // Bullet
    .replace(/–/g, '-')     // En dash
    .replace(/—/g, '-')     // Em dash
    .replace(/'/g, "'")     // Smart quote
    .replace(/'/g, "'")     // Smart quote
    .replace(/"/g, '"')     // Smart quote
    .replace(/"/g, '"');    // Smart quote
};

/**
 * Our clinic-logo proxy (GET /api/organization-logo?key=...) exists on every env's own origin and
 * reads from a SHARED S3 bucket, so the same key resolves identically on dev/staging/prod. A stored
 * logo_path can carry a DIFFERENT env's host (e.g. a staging URL cloned into prod data) — which would
 * make the PDF's fetch below cross-origin, get blocked, and silently drop the logo. Rewrite any
 * absolute /api/organization-logo URL to the current origin so it always loads same-origin.
 */
export function toSameOriginLogoUrl(url: string): string {
  if (typeof window === 'undefined' || !window.location?.origin) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname === '/api/organization-logo' && parsed.origin !== window.location.origin) {
      return `${window.location.origin}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Not a parseable absolute URL (e.g. a relative path) — leave it untouched.
  }
  return url;
}

/**
 * Loads an image URL into a base64 data URL for embedding in PDFs.
 * Returns null on any failure so a missing/invalid logo never breaks PDF generation.
 */
async function loadLogoDataUrl(url?: string): Promise<string | null> {
  if (!url) return null;
  // Already an inline data URL — use as-is (jsPDF accepts it directly).
  if (url.startsWith('data:')) return url;
  try {
    // Our own logo-proxy URLs are pinned to the current origin (so a cross-env host in the stored
    // logo_path can't cause a blocked cross-origin fetch); CORS-enabled remote URLs pass through
    // unchanged. Credentials are included for the authenticated same-origin proxy.
    const res = await fetch(toSameOriginLogoUrl(url), { credentials: 'same-origin' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// PHA-1164: every program the patient is enrolled in, in display order. APCM when the patient
// has an APCM tier. CCM when there is no tier (a CCM clinic), and also alongside APCM Tier 2+,
// whose chronic conditions make the patient CCM-eligible too (billing already pairs G0557 with
// the CCM companion code). Tier 1 is APCM's general care plan, so it is APCM alone. RPM when an
// RPM device is assigned.
export type CarePlanProgram = 'APCM' | 'CCM' | 'RPM';

export const CARE_PLAN_PROGRAM_NAMES: Record<CarePlanProgram, string> = {
  APCM: 'Advanced Primary Care Management (APCM)',
  CCM: 'Chronic Care Management (CCM)',
  RPM: 'Remote Physiologic Monitoring (RPM)',
};

export function deriveCarePlanPrograms(patient: {
  apcm_tier?: number | null;
  has_rpm_device?: boolean | null;
}): CarePlanProgram[] {
  const tier = patient.apcm_tier ?? null;
  const programs: CarePlanProgram[] = [];
  if (tier) programs.push('APCM');
  if (!tier || tier >= 2) programs.push('CCM');
  if (patient.has_rpm_device === true) programs.push('RPM');
  return programs;
}

const joinWithAnd = (items: string[]): string =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// PHA-1164: the Introduction paragraph. `generalOnly` = APCM Tier 1 (general care plan, no
// qualifying chronic condition), which talks about the patient's health, not chronic conditions.
export function buildCarePlanIntroText(
  programs: CarePlanProgram[],
  options: { generalOnly: boolean; conditionCount: number }
): string {
  const { generalOnly, conditionCount } = options;
  const several = programs.length > 1;
  const names = joinWithAnd(programs.map((p) => CARE_PLAN_PROGRAM_NAMES[p]));
  const kind = several ? 'care management programs' : 'a care management program';
  const conditions = conditionCount === 1 ? 'condition' : 'conditions';
  const reason = generalOnly
    ? 'to support your health'
    : `because you have ${conditionCount === 1 ? 'a significant chronic medical condition' : 'significant chronic medical conditions'}`;
  return (
    `You are enrolled in ${names}, ${kind} offered by your insurance ${reason}. ` +
    `${several ? 'These programs allow' : 'This program allows'} our team to monitor your health from home between visits. ` +
    'We check in with you, help manage medications, and coordinate your care to help prevent complications and keep you healthier. ' +
    'This care plan outlines your health goals, medications, lifestyle recommendations, monitoring schedule and tasks ' +
    `based on your ${generalOnly ? 'health' : 'chronic'} ${conditions}. ` +
    'Your care team will review your progress every month and help you stay on track.'
  );
}

// PHA-1195: printing the care plan in the patient's language.
//
// The PDF's built-in Helvetica only covers Latin-1 (accents, ñ, ¿, ç...), so only Latin-script
// languages can be printed translated. Hebrew and Arabic patients keep the English printout
// until a Unicode font with right-to-left shaping is embedded.
const PDF_TRANSLATABLE_LANGUAGES: Record<string, Locale> = { es, fr };

/** The patient's language as a printable primary subtag ('es', 'fr'), or null for English. */
export function resolveCarePlanPdfLanguage(language?: string | null): string | null {
  const primary = (language || '').split('-')[0].toLowerCase();
  return primary && primary in PDF_TRANSLATABLE_LANGUAGES ? primary : null;
}

// Helvetica's WinAnsi range. A translation with a character outside it would print garbage,
// so it is dropped and that text stays English.
const isPrintableInHelvetica = (text: string): boolean =>
  // eslint-disable-next-line no-control-regex
  /^[\x00-\xFF\u0152\u0153\u0160\u0161\u0178\u017D\u017E\u0192\u02C6\u02DC\u2013\u2014\u2018-\u201E\u2020-\u2022\u2026\u2030\u2039\u203A\u20AC\u2122]*$/.test(text);

interface CarePlanPdfI18n {
  // English text (trimmed) -> translation. Absent text prints in English.
  translations?: Record<string, string>;
  // When set, every translatable text is recorded here (the collection pass).
  collect?: Set<string>;
  // Texts written about this one patient (care-team notes, their own goals and barriers). They
  // are translated for this print but never stored in the server's shared cache.
  collectPrivate?: Set<string>;
  locale?: Locale;
  // The collection pass lays nothing out for real, so it skips the logo fetch.
  skipLogo?: boolean;
}

/**
 * PHA-983: builds the Care Plan PDF and returns the jsPDF document without saving it.
 *
 * Extracted so the same bytes can either be downloaded (generateCarePlanPdf) or handed to
 * the EHR push (generateCarePlanPdfBlob). Everything below this line is the original
 * implementation, unchanged apart from returning `doc` instead of calling `doc.save`.
 */
const buildCarePlanPdfDoc = async (
  patient: PatientData,
  i18n: CarePlanPdfI18n = {}
): Promise<{ doc: jsPDF; filename: string }> => {
  const doc = new jsPDF();
  // PHA-1195: every text the patient reads goes through t(); values (names, dates, MRN, codes,
  // medication names and doses) do not.
  const t = (text: string): string => {
    const key = (text ?? '').trim();
    if (!key) return text;
    i18n.collect?.add(key);
    return i18n.translations?.[key] ?? text;
  };
  // Patient-specific free text: translated, but kept out of the shared cache.
  const tPrivate = (text: string): string => {
    const key = (text ?? '').trim();
    if (!key) return text;
    i18n.collectPrivate?.add(key);
    return i18n.translations?.[key] ?? text;
  };
  const fmtDate = (dateString: string): string => formatDate(dateString, i18n.locale);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 20;
  // PHA-1032: not-yet-approved render — drives the DRAFT banner/header/filename below and
  // switches the reviewer-name/review-date fields to their draft sources.
  const is_draft = patient.is_draft === true;

  // Helper function to check if we need a new page
  const checkNewPage = (neededSpace: number = 30): void => {
    if (yPos + neededSpace > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Helper function to add wrapped text and return new Y position
  const addWrappedText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number = 5): number => {
    const lines = doc.splitTextToSize(sanitizeTextForPdf(text), maxWidth);
    doc.text(lines, x, y);
    return y + lines.length * lineHeight;
  };

  // PHA-1195: a bold label and its value on one line. The value sits at `minOffset`, or past
  // the label when a translated label is wider than the English one it was laid out for.
  const drawLabelValue = (label: string, value: string, minOffset: number): void => {
    const shown = t(label);
    doc.setFont('helvetica', 'bold');
    doc.text(shown, margin, yPos);
    const width = doc.getTextWidth(shown);
    const offset = Number.isFinite(width) ? Math.max(minOffset, width + 3) : minOffset;
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + offset, yPos);
  };

  // Helper function to draw a table row with word wrapping
  const drawTableRow = (
    cells: string[],
    colWidths: number[],
    startX: number,
    startY: number,
    fontSize: number = 8,
    isHeader: boolean = false,
    cellPadding: number = 2,
    lineHeight: number = 4
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isHeader ? 'bold' : 'normal');

    // Calculate max row height based on wrapped text
    let maxRowHeight = lineHeight;
    const wrappedCells: string[][] = [];
    
    cells.forEach((cellText, i) => {
      const cellWidth = colWidths[i] - 2 * cellPadding;
      const lines = doc.splitTextToSize(sanitizeTextForPdf(cellText || ''), cellWidth);
      wrappedCells.push(lines);
      const cellHeight = lines.length * lineHeight;
      if (cellHeight > maxRowHeight) {
        maxRowHeight = cellHeight;
      }
    });

    // Check if we need a new page for this row
    if (startY + maxRowHeight + cellPadding * 2 > pageHeight - 20) {
      doc.addPage();
      startY = 20;
      yPos = 20;
    }

    // Calculate total table width
    const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);

    // Draw header background if needed
    if (isHeader) {
      doc.setFillColor(230, 230, 230);
      doc.rect(startX, startY - cellPadding, totalTableWidth, maxRowHeight + cellPadding * 2, 'F');
    }

    // Draw cell borders
    let currentX = startX;
    colWidths.forEach((width) => {
      doc.setDrawColor(200, 200, 200);
      doc.rect(currentX, startY - cellPadding, width, maxRowHeight + cellPadding * 2, 'S');
      currentX += width;
    });

    // Draw text in each cell
    currentX = startX;
    wrappedCells.forEach((lines, i) => {
      const textX = currentX + cellPadding;
      let textY = startY + lineHeight * 0.8;
      lines.forEach((line) => {
        doc.text(line, textX, textY);
        textY += lineHeight;
      });
      currentX += colWidths[i];
    });

    return startY + maxRowHeight + cellPadding * 2;
  };

  // PHA-888: render a muted "As of <date>" note under a section header. Returns the
  // advanced Y position (unchanged when no import date is available, so the note is
  // simply omitted rather than showing a placeholder).
  const renderAsOfNote = (uploadedTs: string | null, y: number): number => {
    if (!uploadedTs) return y;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text(`${t('As of')} ${fmtDate(uploadedTs)}`, margin, y);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    return y + 5;
  };

  // Branding: clinic logo or organization name above the title

  // FIX 3: single local helper to render the org-name text fallback
  const renderOrgNameFallback = (): void => {
    if (patient.organization_name) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(patient.organization_name), pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
    }
  };

  const logoDataUrl = i18n.skipLogo ? null : await loadLogoDataUrl(patient.logo_path);
  if (logoDataUrl) {
    try {
      const maxW = 50;
      const maxH = 16;
      const props = doc.getImageProperties(logoDataUrl);
      // FIX 2: guard against zero/Infinity dimensions
      if (!props.width || !props.height) throw new Error('invalid image dimensions');
      const scale = Math.min(maxW / props.width, maxH / props.height);
      const drawW = props.width * scale;
      const drawH = props.height * scale;
      const x = (pageWidth - drawW) / 2;
      // FIX 1: derive format from MIME type; empty string lets jsPDF auto-detect raster formats
      const fmt = (logoDataUrl.match(/^data:image\/([^;]+)/)?.[1] ?? '').toUpperCase();
      doc.addImage(logoDataUrl, fmt, x, yPos, drawW, drawH);
      yPos += drawH + 10;
    } catch {
      // Bad image — fall through to text fallback
      renderOrgNameFallback(); // FIX 3: use shared helper
    }
  } else {
    renderOrgNameFallback(); // FIX 3: use shared helper
  }

  // Covered-condition rollup for the §3 "Chronic Conditions Managed" bullets.
  const conditionNames = deriveManagedConditionNames(patient);

  // Title — general-plan-aware (PHA-842) and DECOUPLED from §3: a no-diagnosis plan reads
  // "General Care Plan" (matching the in-app heading) rather than "Patient Care Plan for
  // <uncurated Dx laundry list>", while §3 below still enumerates the real conditions. Wrap
  // within the page margins so long Dx lists don't run off the edges (PHA-767).
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const pdfTitle = formatPdfDocumentTitle(patient.care_plan_definitions);
  const titleLines: string[] = doc.splitTextToSize(sanitizeTextForPdf(t(pdfTitle)), contentWidth);
  const titleLineHeight = 8; // ~matches 18pt bold
  titleLines.forEach((line, i) => {
    doc.text(line, pageWidth / 2, yPos + i * titleLineHeight, { align: 'center' });
  });
  // Preserve the original single-line spacing (15) before the rule; add a line for each wrap.
  yPos += (titleLines.length - 1) * titleLineHeight + 15;

  // PHA-1032: prominent banner so a printed/downloaded draft is never mistaken for the
  // approved plan. Placed once, on page 1 only — the running header below repeats it on
  // every page for multi-page drafts.
  if (is_draft) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(180, 0, 0);
    doc.text(t('DRAFT - NOT YET APPROVED'), pageWidth / 2, yPos, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    yPos += 8;
  }

  // Horizontal line
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Patient Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(t('Patient Information'), margin, yPos);
  yPos += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const patientName = `${patient.first_name} ${patient.last_name}`;
  const createdDate = fmtDate(resolveCarePlanCreatedDate(patient));
  
  drawLabelValue('Patient Name:', sanitizeTextForPdf(patientName), 35);
  yPos += 7;

  drawLabelValue('Date of Birth:', fmtDate(patient.date_of_birth), 35);
  yPos += 7;

  drawLabelValue('Care Plan Created:', createdDate, 45);
  yPos += 7;

  // PHA-767: care plan expiration date (mirrors patient-detail card; "Not set" when absent).
  const expirationRaw =
    findGeneralCarePlanDefinition(patient.care_plan_definitions)?.expiration_date ??
    patient.care_plan_definitions?.find((d) => d.expiration_date)?.expiration_date ??
    // PHA-737: draft PDFs have no per-definition expiration; fall back to the on-screen value.
    patient.expiration_date ??
    null;
  const expirationText = expirationRaw ? fmtDate(expirationRaw) : t('Not set');

  // PHA-1032 follow-up: "review" names two different things and they must not be conflated.
  //
  //   Header "Care Plan Review Date" = the due date for creating the NEXT care plan. ODA asked
  //   for that label instead of "Due date"; it is the expiration value above (today+6mo by
  //   default at approve time) and is the same date shown as "Next Review Date" on the care
  //   plan tab. It is a property of the PLAN.
  //
  //   PHA-870 care_plan_reviews[].review_date = when a care team member logged a review note.
  //   It renders ONLY in the "Care Plan Notes" section further down (section 6), never here.
  //   It is a property of a REVIEW RECORD.
  //
  // An earlier pass had the draft header prefer care_plan_reviews[0].review_date, which made
  // entering a review note silently move the plan's due date. Both drafts and approved plans
  // use the expiration-based value; do not re-couple them.
  // Label is ~48.5mm wide at 11pt bold; offset 60 keeps a gap consistent with the other rows.
  drawLabelValue('Care Plan Review Date:', expirationText, 60);
  yPos += 7;

  // PHA-867: "Generated and reviewed by: <clinician name>" for PCMH-enabled clinics.
  // Gated on voice_of_patient_enabled (the per-org "PCMH" flag). The name is the
  // approving clinician, snapshotted at approval time; resolve from the general
  // care plan definition, falling back to any definition that carries a name.
  // Omitted for pre-PHA-867 plans and non-PCMH clinics (reviewed_by_name null).
  // PHA-1032: a draft has no approving clinician yet, so it uses the current user
  // (draft_reviewer_name) instead of a snapshotted care_plan_definitions field.
  const reviewedByName = is_draft
    ? (patient.draft_reviewer_name ?? null)
    : (findGeneralCarePlanDefinition(patient.care_plan_definitions)?.reviewed_by_name ??
       patient.care_plan_definitions?.find((d) => d.reviewed_by_name)?.reviewed_by_name ??
       null);
  if (patient.voice_of_patient_enabled === true && reviewedByName) {
    const reviewedByLabel = t('Generated and reviewed by:');
    doc.setFont('helvetica', 'bold');
    doc.text(reviewedByLabel, margin, yPos);
    const reviewedByOffset = doc.getTextWidth(reviewedByLabel) + 3;
    doc.setFont('helvetica', 'normal');
    doc.text(sanitizeTextForPdf(reviewedByName), margin + reviewedByOffset, yPos);
    yPos += 7;
  }

  // PHA-1164: list every program, one per line.
  const programs = deriveCarePlanPrograms(patient);
  const programsLabel = t(programs.length > 1 ? 'Programs:' : 'Program:');
  doc.setFont('helvetica', 'bold');
  doc.text(programsLabel, margin, yPos);
  const programsLabelWidth = doc.getTextWidth(programsLabel);
  const programsOffset = Number.isFinite(programsLabelWidth) ? Math.max(25, programsLabelWidth + 3) : 25;
  doc.setFont('helvetica', 'normal');
  programs.forEach((program, i) => {
    if (i > 0) yPos += 6;
    doc.text(t(CARE_PLAN_PROGRAM_NAMES[program]), margin + programsOffset, yPos);
  });
  // PHA-767: match the padding below the top rule (10) so the section's open/close gaps are equal.
  yPos += 10;

  // Horizontal line
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Dynamic section numbering (PHA-739: patient-friendly section inserted after Introduction)
  let sectionNum = 1;

  // Introduction Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${sectionNum++}. ${t('Introduction')}`, margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const introText = buildCarePlanIntroText(programs, {
    generalOnly: patient.apcm_tier === 1,
    conditionCount: conditionNames.length,
  });

  yPos = addWrappedText(t(introText), margin, yPos, contentWidth);
  yPos += 10;

  // PHA-739: Patient-Friendly Section (inserted after Introduction, before Chronic Conditions)
  // Extract blob from first definition that has non-empty patient_friendly_plan content.
  const patientFriendlyPlan = (patient.care_plan_definitions || [])
    .find(cpd => {
      const pfp = cpd.patient_friendly_plan;
      if (!pfp) return false;
      return !!(
        (pfp.goals && pfp.goals.trim()) ||
        (pfp.medications && pfp.medications.length > 0) ||
        (pfp.tasks && pfp.tasks.length > 0) ||
        (pfp.barriers && pfp.barriers.trim()) ||
        (pfp.warning_signs && pfp.warning_signs.length > 0) ||
        (pfp.resources && pfp.resources.length > 0)
      );
    })?.patient_friendly_plan ?? null;

  if (patientFriendlyPlan) {
    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum++}. ${sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.SECTION))}`, margin, yPos);
    yPos += 8;

    // Goals
    if (patientFriendlyPlan.goals && patientFriendlyPlan.goals.trim()) {
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.GOALS)), margin, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(t(patientFriendlyPlan.goals), margin, yPos, contentWidth);
      yPos += 6;
    }

    // Medications
    if (patientFriendlyPlan.medications && patientFriendlyPlan.medications.length > 0) {
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.MEDICATIONS)), margin, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      patientFriendlyPlan.medications.forEach((med) => {
        const name = med.name ?? '';
        const reason = med.reason ?? '';
        if (!name && !reason) return;
        checkNewPage(10);
        // Medication names stay as written; the reason is translated.
        const medText = reason ? `${name} - ${t(reason)}` : name;
        yPos = addWrappedText(`• ${medText}`, margin + 3, yPos, contentWidth - 5);
        yPos += 2;
      });
      yPos += 4;
    }

    // Tasks
    if (patientFriendlyPlan.tasks && patientFriendlyPlan.tasks.length > 0) {
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.TASKS)), margin, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      patientFriendlyPlan.tasks.forEach((task) => {
        const instruction = task.instruction ?? '';
        if (!instruction && !task.how_often) return;
        checkNewPage(10);
        const taskText = task.how_often
          ? `${t(instruction)} (${t(task.how_often)})`
          : t(instruction);
        yPos = addWrappedText(`• ${taskText}`, margin + 3, yPos, contentWidth - 5);
        yPos += 2;
      });
      yPos += 4;
    }

    // Challenges (generic plain-language paragraph from the LLM blob).
    // PHA-850: person-centered barriers are NO LONGER listed here — they belong in
    // the "Voice of the patient" section, nested under the goal they relate to (see
    // below). This reverts the PHA-832 behaviour that surfaced barriers out of context.
    if (patientFriendlyPlan.barriers && patientFriendlyPlan.barriers.trim()) {
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.BARRIERS)), margin, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(t(patientFriendlyPlan.barriers), margin, yPos, contentWidth);
      yPos += 6;
    }

    // Warning Signs
    if (patientFriendlyPlan.warning_signs && patientFriendlyPlan.warning_signs.length > 0) {
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeTextForPdf(t(PATIENT_FRIENDLY_HEADINGS.WARNING_SIGNS)), margin, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      patientFriendlyPlan.warning_signs.forEach((ws) => {
        const sign = ws.sign ?? '';
        const action = ws.action ?? '';
        if (!sign && !action) return;
        checkNewPage(10);
        const wsText = sign && action ? `${t(sign)} -> ${t(action)}` : t(sign || action);
        yPos = addWrappedText(`• ${wsText}`, margin + 3, yPos, contentWidth - 5);
        yPos += 2;
      });
      yPos += 4;
    }

    yPos += 6;
  }

  // PHA-795 E4 / PHA-832: "What Matters to You" — person-centered outcome section.
  // Sourced from approved PCO tables (NOT from LLM-generated care-plan JSON). It sits
  // between the plain-English section and the clinical "Chronic Conditions" section.
  // Rendered when the clinic has Voice of the Patient enabled OR there is PCO data;
  // when enabled-but-empty it shows explicit "No goals/barriers were defined" text so
  // the absence is intentional, not a glitch. Omitted entirely for non-VoP clinics.
  const _hasGoals = !!(patient.person_centered_goals && patient.person_centered_goals.length > 0);
  const _hasBarriers = !!(patient.pcm_barriers && patient.pcm_barriers.length > 0);
  if (patient.voice_of_patient_enabled || _hasGoals || _hasBarriers) {
    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum++}. ${t('Voice of the patient')}`, margin, yPos);
    yPos += 8;

    // PHA-850: barriers are grouped under the goal they relate to (each goal is its
    // own block: goal -> tracking question -> its barriers). `unassigned` holds any
    // barriers we cannot attribute to a single goal (multiple goals + no stored link);
    // they render as a shared trailing block rather than being mis-attributed.
    const { perGoal, unassigned } = groupBarriersByGoal(
      patient.person_centered_goals ?? [],
      patient.pcm_barriers ?? []
    );

    const renderBarrierBullet = (barrierText: string, indent: number) => {
      checkNewPage(10);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(`- ${sanitizeTextForPdf(tPrivate(barrierText))}`, indent, yPos, contentWidth - (indent - margin) - 2);
      yPos += 2;
    };

    // Goals — or explicit empty-state.
    if (!_hasGoals) {
      checkNewPage(10);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      yPos = addWrappedText(t('No goals were defined.'), margin + 3, yPos, contentWidth - 5);
      doc.setFont('helvetica', 'normal');
      yPos += 2;
    }
    perGoal.forEach(({ goal, barriers }) => {
      checkNewPage(16);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(`• ${sanitizeTextForPdf(tPrivate(goal.goal_text))}`, margin + 3, yPos, contentWidth - 5);

      // Tracking question: prefer clinician-authored tracking_question, fall back to GAS level_zero.
      const gasDef = patient.gas_definitions?.find(d => d.pco_ref === goal.pco_ref);
      const trackingLabel = goal.tracking_question || gasDef?.level_zero || null;
      if (trackingLabel) {
        checkNewPage(10);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        yPos = addWrappedText(
          `  ${t('How we track progress:')} ${sanitizeTextForPdf(tPrivate(trackingLabel))} ${t('(measured on a 5-point scale)')}`,
          margin + 8,
          yPos,
          contentWidth - 12,
          4
        );
        doc.setFont('helvetica', 'normal');
      }

      // Barriers for THIS goal, nested beneath it.
      if (barriers.length > 0) {
        checkNewPage(10);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        yPos = addWrappedText(`  ${t("Barriers we're working on together:")}`, margin + 8, yPos, contentWidth - 12, 4);
        doc.setFont('helvetica', 'normal');
        barriers.forEach((barrier) => renderBarrierBullet(barrier.barrier_text, margin + 10));
      }
      yPos += 4;
    });

    // Barriers that could not be attributed to a single goal (multi-goal, no link).
    if (unassigned.length > 0) {
      yPos += 2;
      checkNewPage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(t('Barriers We Are Working On Together'), margin, yPos);
      yPos += 6;
      unassigned.forEach((barrier) => renderBarrierBullet(barrier.barrier_text, margin + 3));
    }

    yPos += 6;
  }

  // Chronic Conditions Managed Section
  checkNewPage(40);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${sectionNum++}. ${t('Chronic Conditions Managed')}`, margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(t('You are currently being supported for the following conditions:'), margin, yPos);
  yPos += 8;

  // conditionNames computed above — the real covered conditions (PHA-842: the generic
  // "General care plan"/"global" tokens are filtered out so a no-Dx plan falls to the
  // medical-record line rather than echoing the plan-type label).
  if (conditionNames.length > 0) {
    conditionNames.forEach((name) => {
      checkNewPage(8);
      // Title-case each bullet to match the "Patient Care Plan for …" heading (PHA-767).
      doc.text(`• ${sanitizeTextForPdf(t(formatDiseaseName(name)))}`, margin + 5, yPos);
      yPos += 6;
    });
  } else if (patient.care_plan_definitions && patient.care_plan_definitions.length > 0) {
    doc.text(`• ${t('The chronic conditions listed in your medical record')}`, margin + 5, yPos);
    yPos += 6;
  } else {
    doc.text(`• ${t('No care plans currently defined')}`, margin + 5, yPos);
    yPos += 6;
  }
  yPos += 10;

  // Care Plans by Condition Section
  checkNewPage(40);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${sectionNum++}. ${t('Your Care Plan in Details')}`, margin, yPos);
  yPos += 15;

  // Iterate through care plan definitions
  if (patient.care_plan_definitions && patient.care_plan_definitions.length > 0) {
    const generalPlan = findGeneralCarePlanDefinition(patient.care_plan_definitions);
    const conditionPlans = patient.care_plan_definitions.filter(
      (cpd) => !isGeneralCarePlanDisease(cpd.disease)
    );
    const plansToRender = generalPlan ? [...conditionPlans, generalPlan] : conditionPlans;
    const hasMultiplePlans = plansToRender.length > 1;
    // PHA-832: person-centered (GAS) goals are patient-level, not per-disease. Anchor them
    // to the general plan (or the first plan when there is none) so they appear ONCE under
    // Health Goals + Assessment Questions, alongside the clinical goals/questions.
    const pcoGoalsForPdf = [...(patient.person_centered_goals ?? [])].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );
    plansToRender.forEach((cpd, index) => {
      const isPcoAnchor = (generalPlan ? cpd === generalPlan : index === 0) && pcoGoalsForPdf.length > 0;
      checkNewPage(50);

      // Per-condition sub-header — only rendered when there are multiple distinct plans.
      if (hasMultiplePlans) {
        const conditionLetter = String.fromCharCode(65 + index); // A, B, C, etc.
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(
          `${conditionLetter}. ${sanitizeTextForPdf(t(formatPdfCarePlanSectionTitle(cpd.disease, cpd.diseases ?? [], true, cpd.icd10_codes, cpd.is_general)))}`,
          margin,
          yPos
        );
        yPos += 10;
      }

      // Goals Section (Health Goals table) — clinical goals + person-centered GAS goals.
      if ((cpd.goals && cpd.goals.length > 0) || isPcoAnchor) {
        checkNewPage(50);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(t('Health Goals'), margin, yPos);
        yPos += 8;

        if (cpd.goals && cpd.goals.length > 0) {
          // PHA-866: orgs with "no target dates" drop the Due Date / Achieved? /
          // Achievement Date columns entirely (a compliance requirement).
          const hideTargetDates = patient.no_target_dates_enabled === true;
          // Define column widths optimized for A4 portrait (210mm = ~175mm content)
          // Full order: Measure, Target, Test, Due Date, Achieved?, Achievement Date
          // Total should fit within contentWidth (~175mm)
          const colWidths = hideTargetDates ? [65, 60, 50] : [35, 35, 30, 35, 20, 30];
          const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
          const tableStartX = margin + (contentWidth - totalColWidth) / 2;

          // Draw table header
          yPos = drawTableRow(
            (hideTargetDates
              ? ['Measure', 'Target', 'Test']
              : ['Measure', 'Target', 'Test', 'Due Date', 'Achieved?', 'Achievement Date']
            ).map(t),
            colWidths,
            tableStartX,
            yPos,
            8,
            true
          );

          // Draw table rows for all goals (including achieved ones)
          cpd.goals.forEach((goal) => {
            const achievedMark = goal.achieved ? '[+]' : '';
            const achievedDate = goal.achieved_date ? fmtDate(goal.achieved_date) : '';
            yPos = drawTableRow(
              hideTargetDates
                ? [
                    t(goal.measure || ''),
                    t(goal.target_value || ''),
                    t(goal.test || '')
                  ]
                : [
                    t(goal.measure || ''),
                    t(goal.target_value || ''),
                    t(goal.test || ''),
                    goal.due_date ? fmtDate(goal.due_date) : '',
                    achievedMark,
                    achievedDate
                  ],
              colWidths,
              tableStartX,
              yPos,
              8,
              false
            );
          });
          yPos += 4;
        }

        // PHA-832: person-centered goals, tracked on a 5-point GAS scale (no due date /
        // binary "achieved" — progress is measured via the Voice-of-the-Patient survey).
        if (isPcoAnchor) {
          checkNewPage(12);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'italic');
          yPos = addWrappedText(t('Personal goals (tracked on a 5-point GAS scale):'), margin + 3, yPos, contentWidth - 5);
          doc.setFont('helvetica', 'normal');
          yPos += 2;
          pcoGoalsForPdf.forEach((goal) => {
            checkNewPage(10);
            yPos = addWrappedText(`• ${sanitizeTextForPdf(tPrivate(goal.goal_text))}`, margin + 3, yPos, contentWidth - 5);
            yPos += 2;
          });
        }
        yPos += 8;
      }

      // Assessment Questions Section — clinical questions + person-centered GAS check-ins.
      if ((cpd.questions && cpd.questions.length > 0) || isPcoAnchor) {
        checkNewPage(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(t('Assessment Questions'), margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        if (cpd.questions && cpd.questions.length > 0) {
          cpd.questions.forEach((question) => {
            checkNewPage(12);
            const questionText = `• ${t(question.prompt)}`;
            yPos = addWrappedText(questionText, margin + 3, yPos, contentWidth - 5);
            yPos += 2;
          });
        }
        // PHA-832: GAS tracking questions (answered on a 5-point scale in the survey).
        if (isPcoAnchor) {
          pcoGoalsForPdf.forEach((goal) => {
            checkNewPage(12);
            const tq = goal.tracking_question || `How well are you managing your goal: ${goal.goal_text}?`;
            yPos = addWrappedText(`• ${sanitizeTextForPdf(tPrivate(tq))} ${t('(5-point scale)')}`, margin + 3, yPos, contentWidth - 5);
            yPos += 2;
          });
        }
        yPos += 6;
      }

      // Tasks Section (Patient Tasks only)
      if (cpd.tasks && cpd.tasks.length > 0) {
        // Filter for patient-assignee tasks (include both completed and not completed)
        const patientTasks = cpd.tasks.filter(
          (task) => task.assignee?.toLowerCase() === 'patient'
        );
        
        if (patientTasks.length > 0) {
          checkNewPage(30);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text(t('Tasks'), margin, yPos);
          yPos += 8;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          patientTasks.forEach((task) => {
            checkNewPage(12);
            let taskText = '';
            if (task.completed) {
              const completedDate = task.completed_date ? ` (${t('Completed:')} ${fmtDate(task.completed_date)})` : '';
              taskText = `[+] ${t(task.task)}${completedDate}`;
            } else {
              taskText = t(task.task);
            }
            yPos = addWrappedText(taskText, margin + 3, yPos, contentWidth - 5);
            yPos += 2;
          });
          yPos += 6;
        }
      }

      yPos += 10;
    });

  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(t('No care plans are currently defined for this patient.'), margin, yPos);
    yPos += 15;
  }

  // PHA-870/PHA-1109: Care Plan Notes (renamed from "Care Plan Review" so it is not confused
  // with the header's "Care Plan Review Date" above) — care-team-authored records, a section
  // directly following "Your Care Plan in Details" (per ODA requirement). Only rendered when
  // reviews exist; reviews arrive newest-first from the backend (review_date DESC). Labels
  // mirror the on-screen cards in PatientDetail.tsx.
  if (patient.care_plan_reviews && patient.care_plan_reviews.length > 0) {
    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum++}. ${t('Care Plan Notes')}`, margin, yPos);
    yPos += 12;

    patient.care_plan_reviews.forEach((review) => {
      checkNewPage(30);
      // Note date sub-header.
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${t('Note Date:')} ${review.review_date ? fmtDate(review.review_date) : '-'}`,
        margin,
        yPos
      );
      yPos += 7;

      // Patient Comments — free text, may contain line breaks (addWrappedText/splitTextToSize
      // preserve '\n'). Fall back to '-' for blank/whitespace, matching the on-screen card.
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(t('Patient Comments:'), margin + 3, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(
        review.patient_comments?.trim() ? tPrivate(review.patient_comments) : '-',
        margin + 3,
        yPos,
        contentWidth - 5
      );
      yPos += 4;

      // Notes / Actions Taken — free text (care-team notes and actions).
      checkNewPage(16);
      doc.setFont('helvetica', 'bold');
      doc.text(t('Notes / Actions Taken:'), margin + 3, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      yPos = addWrappedText(
        review.action_taken?.trim() ? tPrivate(review.action_taken) : '-',
        margin + 3,
        yPos,
        contentWidth - 5
      );
      yPos += 10;
    });
  }

  // PHA-865: Full Problem List + Full Medication List — PCMH compliance requirement.
  // Gated on the per-org "PCMH Voice of patient intake" flag (voice_of_patient_enabled),
  // the same flag that gates the "What Matters to You" section above. Section numbers are
  // dynamic (sectionNum++), so with the patient-friendly + Voice-of-Patient sections present
  // these typically render as §6/§7 rather than the ticket's literal "5 and 6".
  if (patient.voice_of_patient_enabled) {
    // §N — Full Problem List (active problems only). Backend treats a missing/blank
    // clinical_status as active, so mirror that here.
    const isActiveProblem = (p: Problem): boolean => {
      const status = (p.clinical_status || '').toLowerCase().trim();
      return status === '' || status === 'active';
    };
    const activeProblems = (patient.problems ?? []).filter(isActiveProblem);

    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum++}. ${t('Full Problem List')}`, margin, yPos);
    yPos += 6;

    // PHA-888: "As of <date>" the list was pulled — the most recent import timestamp
    // across all problems (not just active ones), rendered in smaller, muted text.
    yPos = renderAsOfNote(mostRecentUploadedTs(patient.problems ?? []), yPos);
    yPos += 2;

    if (activeProblems.length > 0) {
      // Columns: Problem | ICD-10 | Clinical Status | Onset Date (sum 170mm = contentWidth)
      const problemColWidths = [70, 24, 40, 36];
      const problemTableStartX = margin + (contentWidth - problemColWidths.reduce((a, b) => a + b, 0)) / 2;
      yPos = drawTableRow(
        ['Problem', 'ICD-10', 'Clinical Status', 'Onset Date'].map(t),
        problemColWidths,
        problemTableStartX,
        yPos,
        8,
        true
      );
      activeProblems.forEach((problem) => {
        yPos = drawTableRow(
          [
            t(problem.problem || ''),
            problem.icd10_code || '',
            t(problem.clinical_status || 'Active'),
            problem.onset_date ? fmtDate(problem.onset_date) : '',
          ],
          problemColWidths,
          problemTableStartX,
          yPos,
          8,
          false
        );
      });
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(t('No active problems recorded.'), margin, yPos);
      yPos += 6;
    }
    yPos += 12;

    // §N — Full Medication List (all medications; no status field exists to filter on).
    const medications = patient.medications ?? [];

    checkNewPage(40);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum++}. ${t('Full Medication List')}`, margin, yPos);
    yPos += 6;

    // PHA-888: "As of <date>" the medication list was pulled.
    yPos = renderAsOfNote(mostRecentUploadedTs(medications), yPos);
    yPos += 2;

    if (medications.length > 0) {
      // Columns: Medication | Dosage | Start Date | End Date (sum 170mm = contentWidth)
      const medColWidths = [66, 46, 29, 29];
      const medTableStartX = margin + (contentWidth - medColWidths.reduce((a, b) => a + b, 0)) / 2;
      yPos = drawTableRow(
        ['Medication', 'Dosage', 'Start Date', 'End Date'].map(t),
        medColWidths,
        medTableStartX,
        yPos,
        8,
        true
      );
      medications.forEach((medication) => {
        yPos = drawTableRow(
          [
            medication.medication || '',
            medication.dosage || t('Not specified'),
            medication.start_date ? fmtDate(medication.start_date) : '',
            medication.end_date ? fmtDate(medication.end_date) : t('Ongoing'),
          ],
          medColWidths,
          medTableStartX,
          yPos,
          8,
          false
        );
      });
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(t('No medications recorded.'), margin, yPos);
      yPos += 6;
    }
    yPos += 12;
  }

  // PHA-942: static "Printed and handed to the patient" note, bottom-anchored on the
  // LAST page just above the running footer. Three independent gates:
  //   1. PCMH clinics only  -> voice_of_patient_enabled (the per-org "PCMH" flag)
  //   2. finalized plan     -> is_finalized_plan, set only by the approved-plan download
  //   3. patient consented  -> consent_status === 'collected'
  //   4. PHA-983: not the EHR copy -> is_ehr_copy. A plan pushed to eCW satisfies gates
  //      1-3 exactly as the printed copy does, but it was filed into a chart, not handed
  //      to anyone — so the note would assert something that never happened, inside a
  //      permanent medical record. Verified 2026-08-13 that this is not hypothetical:
  //      Wallabout-ODA (org 54359) is on eCW AND has voice_of_patient_enabled = true.
  // Runs BEFORE the header/footer finalize pass so any page added here is counted in
  // the pagination totals.
  if (
    patient.voice_of_patient_enabled === true &&
    patient.is_finalized_plan === true &&
    patient.consent_status === 'collected' &&
    patient.is_ehr_copy !== true
  ) {
    // Footer sits at pageHeight - 10; the note goes 8mm above it. Body content is laid
    // out down to pageHeight - 20, so start a fresh page when the last page's content
    // would collide with the note's line.
    const printedNoteY = pageHeight - 18;
    if (yPos > printedNoteY - 6) {
      doc.addPage();
      yPos = 20;
    }
    doc.setPage(doc.getNumberOfPages());
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(0, 0, 0);
    doc.text(t('Printed and handed to the patient'), margin, printedNoteY);
  }

  // PHA-888: stamp a running header + footer on every page. Done as a finalize pass
  // (after all content is laid out) so the total page count is known for pagination.
  //   Header: "Patient Care plan created on MMM/DD/YYYY"
  //   Footer: patient MRN (left) + "N out of TOTAL" pagination (right)
  // patient_id IS the MRN in this system. The header date mirrors the in-body
  // "Care Plan Created" row — PHA-944: the plan's own date, not the print date.
  const headerText = `${is_draft ? `${t('DRAFT')} - ` : ''}${t('Patient Care plan created on')} ${format(
    new Date(resolveCarePlanCreatedDate(patient)),
    'MMM/dd/yyyy',
    { locale: i18n.locale }
  )}`;
  const footerMrn = `MRN: ${sanitizeTextForPdf(ehrMrnFor(patient.patient_id ?? ''))}`;
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    // Header — top margin, centered.
    doc.text(sanitizeTextForPdf(headerText), pageWidth / 2, 10, { align: 'center' });
    // Footer — bottom margin; MRN left, pagination right.
    doc.text(footerMrn, margin, pageHeight - 10);
    doc.text(`${p} ${t('out of')} ${totalPages}`, pageWidth - margin, pageHeight - 10, {
      align: 'right',
    });
    doc.setTextColor(0, 0, 0);
  }

  // PHA-983: the filename is derived here (rather than by the caller) so the downloaded
  // file and the copy filed into the EHR are named from the same inputs. Returned alongside
  // the doc rather than stashed in module scope — two concurrent renders would race.
  const sanitizedName = patientName.replace(/[^a-zA-Z0-9]/g, '_');
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  // PHA-1032: DRAFT prefix so a downloaded/filed draft is never mistaken for the approved plan.
  const filename = `${is_draft ? 'DRAFT_' : ''}CarePlan_${sanitizedName}_${dateStr}.pdf`;

  return { doc, filename };
};

/**
 * PHA-1195: translations for printing this patient's care plan in their language.
 *
 * Runs the layout once to collect every translatable text, then asks the backend for those
 * texts in the patient's language (cached server-side). Returns {} for English, for a language
 * the PDF font cannot print, and on any failure: the care plan then prints in English, exactly
 * as before, rather than not printing at all.
 */
export const resolveCarePlanPdfI18n = async (patient: PatientData): Promise<CarePlanPdfI18n> => {
  const language = resolveCarePlanPdfLanguage(patient.language);
  if (!language || !patient.patient_id) return {};
  try {
    const collect = new Set<string>();
    const collectPrivate = new Set<string>();
    await buildCarePlanPdfDoc(patient, { collect, collectPrivate, skipLogo: true });
    const { translations } = await patientApi.translateCarePlanPdfTexts(
      patient.patient_id,
      Array.from(collect).filter((text) => !collectPrivate.has(text)),
      Array.from(collectPrivate)
    );
    const printable: Record<string, string> = {};
    Object.entries(translations || {}).forEach(([english, translated]) => {
      if (typeof translated === 'string' && translated.trim() && isPrintableInHelvetica(translated)) {
        printable[english] = translated;
      }
    });
    return { translations: printable, locale: PDF_TRANSLATABLE_LANGUAGES[language] };
  } catch (err) {
    console.error('PHA-1195: care plan translation failed; printing in English:', err);
    return {};
  }
};

/**
 * Generates a Care Plan PDF document for a patient
 * @param patient - Patient data including problems and care plan definitions
 * @returns Promise<void> - triggers browser download of the PDF
 */
export const generateCarePlanPdf = async (patient: PatientData): Promise<void> => {
  const { doc, filename } = await buildCarePlanPdfDoc(patient, await resolveCarePlanPdfI18n(patient));
  doc.save(filename);
};

/**
 * PHA-983: the same Care Plan PDF as a Blob, for pushing to the EHR instead of downloading.
 *
 * Callers should pass `is_ehr_copy: true` so the "Printed and handed to the patient" note is
 * suppressed on a copy that goes into a chart rather than into someone's hands.
 */
export const generateCarePlanPdfBlob = async (
  patient: PatientData
): Promise<{ blob: Blob; filename: string }> => {
  // PHA-1195: the copy filed into the EHR stays in English — it is read by clinicians, and it
  // must match the English plan they reviewed. Only the printout handed to the patient is translated.
  const i18n = patient.is_ehr_copy === true ? {} : await resolveCarePlanPdfI18n(patient);
  const { doc, filename } = await buildCarePlanPdfDoc(patient, i18n);
  return { blob: doc.output('blob'), filename };
};

export default generateCarePlanPdf;
